import React from 'react';
import { Tooltip } from 'antd';
import {
    CheckCircleOutlined,
    NodeIndexOutlined,
    EditOutlined,
    SearchOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';

/** `simple` = single search string, no rewriter, no dual semantic; `dual_semantic` = multi-query RRF; `query_rewrite` = LLM rewrite. */
export function chatRetrievalStrategyFromMetadata(documentMetadata) {
    const dm = documentMetadata && typeof documentMetadata === 'object' ? documentMetadata : {};
    if (dm.rag_query_rewrite === true) return 'query_rewrite';
    if (dm.rag_semantic_dual_query === true) return 'dual_semantic';
    if (dm.rag_semantic_dual_query === false && dm.rag_query_rewrite === false) return 'simple';
    return 'dual_semantic';
}

/** Default prior-user window size for standard search (matches backend `RAG_CLASSIC_MAX_PRIOR_USER_LINES`). */
export const DEFAULT_CLASSIC_MAX_PRIOR_USER_LINES = 8;

export function classicMaxPriorUserLinesFromMetadata(documentMetadata) {
    const dm = documentMetadata && typeof documentMetadata === 'object' ? documentMetadata : {};
    const v = dm.rag_classic_max_prior_user_lines;
    if (typeof v === 'number' && !Number.isNaN(v)) {
        return Math.max(1, Math.min(32, v));
    }
    if (v != null && String(v).trim() !== '') {
        const n = parseInt(String(v), 10);
        if (!Number.isNaN(n)) return Math.max(1, Math.min(32, n));
    }
    return DEFAULT_CLASSIC_MAX_PRIOR_USER_LINES;
}

function normalizeStrategy(value) {
    if (value === 'query_rewrite') return 'query_rewrite';
    if (value === 'dual_semantic') return 'dual_semantic';
    return 'simple';
}

function InfoTip({ title }) {
    return (
        <Tooltip title={title}>
            <InfoCircleOutlined className="chat-retrieval-strategy-info" aria-label="More detail" />
        </Tooltip>
    );
}

/**
 * One choice among simple, rewriter, or multi-query semantic. Label is supplied by the parent (e.g. Form.Item).
 * Cards are stacked full width for readability (see CSS).
 */
export default function ChatRetrievalStrategySection({
    value,
    onChange,
    disabled,
    compact,
    children,
}) {
    const v = normalizeStrategy(value);

    return (
        <div className={`chat-retrieval-strategy-section${compact ? ' chat-retrieval-strategy-section--compact' : ''}`}>
            <div
                className="create-collection-choice-grid chat-retrieval-strategy-grid"
                role="radiogroup"
                aria-label="Chat search"
            >
                <button
                    type="button"
                    className={`create-collection-choice-card chat-retrieval-strategy-card ${v === 'simple' ? 'is-selected' : ''}`}
                    onClick={() => onChange?.('simple')}
                    disabled={disabled}
                    aria-checked={v === 'simple'}
                    role="radio"
                >
                    <div className="create-collection-choice-card-icon create-collection-choice-card-icon-vector">
                        <SearchOutlined aria-hidden />
                    </div>
                    <div className="create-collection-choice-card-body">
                        <span className="create-collection-choice-card-title">
                            Standard search
                            <InfoTip title="Builds one search string: your latest message first, then up to N earlier user questions (you choose N below). No rewriter and no second embedding search." />
                        </span>
                        <span className="create-collection-choice-card-desc">
                            One search from the latest message plus recent questions you asked.
                        </span>
                    </div>
                    {v === 'simple' && (
                        <CheckCircleOutlined className="create-collection-choice-card-check" aria-hidden />
                    )}
                </button>
                <button
                    type="button"
                    className={`create-collection-choice-card chat-retrieval-strategy-card ${v === 'query_rewrite' ? 'is-selected' : ''}`}
                    onClick={() => onChange?.('query_rewrite')}
                    disabled={disabled}
                    aria-checked={v === 'query_rewrite'}
                    role="radio"
                >
                    <div className="create-collection-choice-card-icon create-collection-choice-card-icon-pageindex">
                        <EditOutlined aria-hidden />
                    </div>
                    <div className="create-collection-choice-card-body">
                        <span className="create-collection-choice-card-title">
                            Query rewriter
                            <InfoTip title="A small model reads the thread and writes one clear search question before lookup. Useful when follow-ups are short or refer back to earlier topics." />
                        </span>
                        <span className="create-collection-choice-card-desc">
                            Optional model step to rewrite the thread into one question before search.
                        </span>
                    </div>
                    {v === 'query_rewrite' && (
                        <CheckCircleOutlined className="create-collection-choice-card-check" aria-hidden />
                    )}
                </button>
                <button
                    type="button"
                    className={`create-collection-choice-card chat-retrieval-strategy-card ${v === 'dual_semantic' ? 'is-selected' : ''}`}
                    onClick={() => onChange?.('dual_semantic')}
                    disabled={disabled}
                    aria-checked={v === 'dual_semantic'}
                    role="radio"
                >
                    <div className="create-collection-choice-card-icon create-collection-choice-card-icon-vector">
                        <NodeIndexOutlined aria-hidden />
                    </div>
                    <div className="create-collection-choice-card-body">
                        <span className="create-collection-choice-card-title">
                            Two searches, merged results
                            <InfoTip title="Runs two document searches (latest line vs. full thread context) and merges the best matches. Strong for long chats and quick follow-ups. No rewriter." />
                        </span>
                        <span className="create-collection-choice-card-desc">
                            Two embedding searches, then combined ranking. No rewriter.
                        </span>
                    </div>
                    {v === 'dual_semantic' && (
                        <CheckCircleOutlined className="create-collection-choice-card-check" aria-hidden />
                    )}
                </button>
            </div>
            {children}
        </div>
    );
}
